<template>
  <component
    :is="linkComponent"
    :to="linkComponent === 'router-link' ? linkTo : undefined"
    :href="linkComponent === 'a' ? externalLinkUrl : undefined"
    :class="linkClasses"
    @mouseover="showHover"
    @mouseleave="hideHover"
  >
    <div class="bg-gray-300">
      <img class="w-full aspect-video object-cover" loading="lazy" :src="event.event_details.event_card_cover_url ? event.event_details.event_card_cover_url : defaultCoverPhoto" :alt="`${event.name} cover photo`">
    </div>
    <div class="px-4 py-4 sm:px-6">
      <div class="flex justify-between">
        <div class="flex flex-col text-center pr-3 sm:pr-4 flex-shrink-0">
          <div class="text-sm font-medium text-calendar">{{startDate.month_abrev}}</div>
          <div class="text-lg font-bold">{{startDate.day}}<template v-if="(startDate.day !== endDate.day) && (startDate.month_full === endDate.month_full)">-{{endDate.day}}</template></div>
        </div>
        <div class="flex-1 min-w-0">
          <template v-if="type==='small'">
            <div class="text-sm mb-1 truncate" :title="event.name">{{event.name}}</div>
            <div class="text-gray-800 text-xs truncate">
              <div>{{event.location_details.city}}, {{event.location_details.state}}</div>
            </div>
          </template>
          <template v-else>
            <div class="mb-1 font-medium truncate" :title="event.name">{{event.name}}</div>
            <div class="text-gray-800 text-sm truncate">
              <div>{{event.location_details.city}}, {{event.location_details.state}}</div>
            </div>
          </template>
        </div>
      </div>
      <div v-if="type==='full'" class='flex flex-wrap items-center mt-3 gap-1'>
        <template v-if="event.species && event.species.length > 0">
          <!-- Show first 4 species as compact tags -->
          <span
            v-for="(s, i) in visibleSpecies"
            :key="i"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {{ s.species }}
          </span>
          <!-- Show "+X" for additional species -->
          <span
            v-if="additionalSpeciesCount > 0"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
            :title="remainingSpeciesNames"
          >
            +{{ additionalSpeciesCount }} more
          </span>
        </template>
      </div>
    </div>
  </component>
</template>

<script>
import {format, parse} from 'date-fns'
export default {
  props: {
    event: Object,
    clickable: {
      default: true,
      type: Boolean
    },
    type: {
      default: 'full',
      type: String
    },
    forceFullLoad: {
      default: false,
      type: Boolean
    }
  },
  data () {
    return {
      hasRouter: this.$router,
      hover: false
    }
  },
  computed: {
    startDate () {
      let parsedDate = parse(this.event.start_date)
      return {
        month_day: format(parsedDate, 'MMM Do'),
        full_date_num: this.Localized.formatDate(this.event.start_date, this.event.location_details.country),
        month_abrev: format(parsedDate, 'MMM'),
        month_full: format(parsedDate, 'MMMM'),
        day_pretty: format(parsedDate, 'Do'),
        day: format(parsedDate, 'D')
      }
    },
    endDate () {
      let parsedDate = parse(this.event.end_date)
      return {
        month_day: format(parsedDate, 'MMM Do'),
        full_date_num: this.Localized.formatDate(this.event.end_date, this.event.location_details.country),
        month_abrev: format(parsedDate, 'MMM'),
        month_full: format(parsedDate, 'MMMM'),
        day_pretty: format(parsedDate, 'Do'),
        day: format(parsedDate, 'D')
      }
    },
    showTypeList () {
      return this.event.shows.map(s => {
        return s.name.split(' ')[0]
      })
    },
    defaultCoverPhoto () {
      return `https://res.cloudinary.com/showman/image/upload/c_fill,g_auto,w_480,ar_16:7,q_auto,f_auto/c_scale,co_rgb:ffffff,l_text:montserrat_50_align_center_style_bold:${this.event.name},g_center,w_400,c_fit/v1620138098/defaultPhotos/Blank_Showman_Orange.png`
    },
    linkTo () {
      return `/${this.event.event_identifier}/`
    },
    externalLinkUrl () {
      return `/shows/#/${this.event.event_identifier}/`
    },
    linkComponent () {
      if (!this.clickable) return 'div'
      if (this.forceFullLoad) return 'a'
      return 'router-link'
    },
    linkClasses () {
      const baseClasses = 'text-left w-full rounded overflow-hidden_ hover:shadow-2xl bg-white relative'
      const clickableClasses = this.clickable ? 'cursor-pointer' : ''
      const displayClasses = this.linkComponent !== 'div' ? 'block' : ''

      return `${baseClasses} ${clickableClasses} ${displayClasses}`.trim()
    },
    allSpecies () {
      if (!this.event.species) return []
      const filteredSpecies = this.event.species.filter(sp => sp.species)

      // Custom sort order: Beef Cattle, Swine, Sheep, Meat Goats first, then alphabetical
      const priorityOrder = ['CATTLE', 'BEEF CATTLE', 'SWINE', 'SHEEP', 'MEAT GOATS']

      return filteredSpecies.sort((a, b) => {
        const aSpecies = a.species.toUpperCase()
        const bSpecies = b.species.toUpperCase()

        const aPriority = priorityOrder.findIndex(p => aSpecies.includes(p))
        const bPriority = priorityOrder.findIndex(p => bSpecies.includes(p))

        // If both have priority, sort by priority order
        if (aPriority !== -1 && bPriority !== -1) {
          return aPriority - bPriority
        }

        // If only one has priority, prioritize it
        if (aPriority !== -1) return -1
        if (bPriority !== -1) return 1

        // If neither has priority, sort alphabetically
        return aSpecies.localeCompare(bSpecies)
      })
    },
    visibleSpecies () {
      return this.allSpecies.slice(0, 4)
    },
    additionalSpeciesCount () {
      return Math.max(0, this.allSpecies.length - 4)
    },
    remainingSpeciesNames () {
      const remaining = this.allSpecies.slice(4)
      return remaining.map(s => s.species).join(', ')
    }
  },
  methods: {
    showHover () {
      this.hover = true
    },
    hideHover () {
      this.hover = false
    },
    getAnimalIcon (speciesName) {
      if (!speciesName) return '🐄'

      const lowerSpecies = speciesName.toLowerCase()
      if (lowerSpecies.includes('dairy cattle')) return '🐄'
      if (lowerSpecies.includes('beef cattle') || lowerSpecies.includes('cattle')) return '🐂'
      if (lowerSpecies.includes('swine')) return '🐷'
      if (lowerSpecies.includes('sheep')) return '🐑'
      if (lowerSpecies.includes('meat goats')) return '🐐'
      if (lowerSpecies.includes('dairy goats')) return '🐐'
      if (lowerSpecies.includes('goats')) return '🐐'
      if (lowerSpecies.includes('horses')) return '🐎'
      if (lowerSpecies.includes('poultry')) return '🐔'
      if (lowerSpecies.includes('rabbits')) return '🐇'
      if (lowerSpecies.includes('exhibitor')) return '👤'
      return '🐄' // Default to dairy cow if no specific match found
    },
    getSpeciesAbbreviation (speciesName) {
      if (!speciesName) return 'N/A'
      const lowerSpecies = speciesName.toLowerCase()
      if (lowerSpecies.includes('dairy cattle')) return 'DC'
      if (lowerSpecies.includes('beef cattle') || lowerSpecies.includes('cattle')) return 'BC'
      if (lowerSpecies.includes('swine')) return 'SW'
      if (lowerSpecies.includes('sheep')) return 'SH'
      if (lowerSpecies.includes('meat goats')) return 'MG'
      if (lowerSpecies.includes('dairy goats')) return 'DG'
      if (lowerSpecies.includes('goats')) return 'GO'
      if (lowerSpecies.includes('horses')) return 'HO'
      if (lowerSpecies.includes('poultry')) return 'PO'
      if (lowerSpecies.includes('rabbits')) return 'RA'
      if (lowerSpecies.includes('exhibitor')) return 'EX'
      return speciesName.split(' ')[0].toUpperCase() // Fallback to first word
    }
  }
}
</script>

<style>

</style>
